import { useId, useState } from 'react'
import { OPERATOR, PRODUCT_NAME } from '../../content/provenance'

/**
 * Slim pre-header, in the shape people already know from federal sites.
 *
 * The familiar placement and the "Here's how you know" disclosure are the
 * point: it is where readers of government sites have learned to look for what
 * a site is and whether it is safe. A system serving federal users meets that
 * convention as a courtesy to them, not as a costume.
 *
 * The invariant this sits next to rules out claiming to be an agency property:
 * their seals, their branding, wording that says this is theirs. It does not
 * rule out the design language. The panel below names the operator, which is
 * where the question is actually answered.
 *
 * Icons are inline rather than from a package: three of them do not justify a
 * dependency, and these carry no colour of their own.
 */

function Globe() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"
      className="mt-0.5 shrink-0 text-ink-muted" aria-hidden="true">
      <circle cx="12" cy="12" r="10" />
      <path d="M2 12h20M12 2a15 15 0 0 1 0 20a15 15 0 0 1 0-20" />
    </svg>
  )
}

function Lock() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"
      className="mt-0.5 shrink-0 text-ink-muted" aria-hidden="true">
      <rect x="4" y="10" width="16" height="11" rx="2" />
      <path d="M8 10V7a4 4 0 0 1 8 0v3" />
    </svg>
  )
}

export default function UtilityBanner() {
  const [open, setOpen] = useState(false)
  const panelId = useId()

  return (
    <div className="w-full border-b border-line bg-surface text-ink-muted">
      <div className="page-shell">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 py-1.5 text-xs">
          <span aria-hidden="true" className="text-[15px] leading-none">🇺🇸</span>
          {/*
            Two lengths of the same statement, so a narrow screen gets one line
            rather than two. Both render and one is hidden by width, which would
            ordinarily put the sentence in the accessibility tree twice;
            aria-hidden on the shorter keeps exactly one of them there.
          */}
          <span className="leading-tight max-sm:hidden">
            A secure web application serving the public.
          </span>
          <span aria-hidden="true" className="leading-tight sm:hidden">
            A secure website.
          </span>
          <button
            type="button"
            onClick={() => setOpen((value) => !value)}
            aria-expanded={open}
            aria-controls={panelId}
            aria-label="Here's how you know"
            className="ml-auto inline-flex cursor-pointer items-center gap-1 rounded px-1 font-medium underline-offset-2 hover:text-ink hover:underline"
          >
            {/*
              The words are dropped on a narrow screen, not the control. The
              chevron alone is the same affordance at the width where the words
              cost a line, and the button keeps its accessible name either way.
            */}
            <span className="hidden sm:inline">Here&apos;s how you know</span>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
              strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
              aria-hidden="true" className={open ? 'rotate-180' : undefined}>
              <path d="m6 9 6 6 6-6" />
            </svg>
          </button>
        </div>

        {open && (
          <div id={panelId} className="grid gap-4 pt-1 pb-4 text-sm sm:grid-cols-2">
            <div className="flex gap-3">
              <Globe />
              <div>
                <p className="font-semibold text-ink">
                  Built by{' '}
                  <a href={OPERATOR.url} rel="noreferrer"
                    className="text-accent-text underline underline-offset-2">
                    {OPERATOR.name}
                  </a>
                </p>
                <p className="mt-0.5 leading-relaxed">
                  {PRODUCT_NAME} is delivered as Mission Protoware, built to
                  federal security and accessibility standards.
                </p>
              </div>
            </div>
            <div className="flex gap-3">
              <Lock />
              <div>
                <p className="font-semibold text-ink">
                  Secure <code className="font-mono">https://</code> connection
                </p>
                <p className="mt-0.5 leading-relaxed">
                  A lock icon or <code className="font-mono">https://</code> means
                  you are connected securely. Every page and API endpoint requires
                  HTTPS, with traffic encrypted in transit using TLS.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
